import { FunctionComponent, useMemo } from 'react';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import VerticalContainer from '../../common/VerticalContainer';
import PageContainer from '../../productPage/tableComponents/PageContainer';
import SideBySideSection, { ImageBox } from '../../common/SideBySideSection';
import { Box, Typography } from '@mui/material';
import { ApplicationsTempSensingForElectronicsContent } from '../../../graphql/pimcore/modules/siteContent/types/ApplicationsContent';
import RichText from '../../common/RichText';
import { splitHtmlByParagraphCount } from '../../../utils/splitHtml';

interface OverViewPageProps {
    contentTree: ApplicationsTempSensingForElectronicsContent;
}

const IMAGE_HEIGHT = 298; // 與右側圖片高度同步

const titleStyle = {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '100%',
    letterSpacing: '0em',
};

const normalTextStyle = {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '1.3rem',
    letterSpacing: '0em',
};

const OverViewPage: FunctionComponent<OverViewPageProps> = ({ contentTree }) => {
    const { isMaxScreen, isScreenXxl, isScreenXl, isScreenLg, isScreenMd, isScreenSm } =
        useResponsive();

    const width = (() => {
        if (isMaxScreen) return '1185px';
        if (isScreenXxl) return '1185px';
        if (isScreenXl) return '1125px';
        if (isScreenLg) return '885px';
        if (isScreenMd) return '650px';
        if (isScreenSm) return '422px';
        return '320px';
    })();

    const variant = (() => {
        if (isMaxScreen) return '60-40';
        if (isScreenXxl) return '60-40';
        if (isScreenXl) return '50-50';
        if (isScreenLg) return '50-50';
        if (isScreenMd) return '50-50';
        if (isScreenSm) return '50-50';
        return '50-50';
    })();

    const pic_width = (() => {
        if (isMaxScreen) return '520px';
        if (isScreenXxl) return '520px';
        if (isScreenXl) return '520px';
        if (isScreenLg) return '440px';
        if (isScreenMd) return '650px';
        if (isScreenSm) return '422px';
        return '320px';
    })();

    const overviewHtml =
        contentTree.overviewMainText?.content ??
        contentTree.overviewMainText?.sanitizedContent ??
        '';

    // 將前兩個 <p> 放在左欄，其餘放到下方延伸區
    const { topHtml, bottomHtml } = useMemo(
        () => splitHtmlByParagraphCount(overviewHtml, 2),
        [overviewHtml]
    );

    return (
        <>
            <VerticalContainer
                containerName={'YnexOverview'}
                sx={{
                    padding: 0,
                    margin: 0,
                    height: 'auto',
                    backgroundColor: '#FFFFFF',
                }}
            >
                <PageContainer
                    sx={{
                        paddingTop: '3rem',
                        paddingBottom: '2rem',
                        backgroundColor: '#F2F2F2',
                    }}
                    innerSx={{ width, display: 'flex', flexDirection: 'column', rowGap: '24px' }}
                >
                    {/* 左：等高標題；右：圖片 */}
                    <SideBySideSection
                        containerName="overview_section_1"
                        /** LG / XL 欄距縮小 */
                        compactColumnGapOnLgXl="16px"
                        /** 垂直時圖片在上（右欄優先） */
                        stackedOrder="right-first"
                        variant={variant}
                        alignY="start" // 重要：頂端對齊
                        left={
                            <Box data-testid={'overviewTitle--area'}>
                                <Typography sx={titleStyle}>
                                    {contentTree.overviewTitle.content}
                                </Typography>

                                <RichText html={topHtml} />
                            </Box>
                        }
                        right={
                            <ImageBox
                                src={contentTree.overviewMainImage.imageUrl}
                                alt="overview"
                                sx={{
                                    height: IMAGE_HEIGHT, // 固定圖片高度
                                    width: pic_width,
                                }}
                            />
                        }
                    />
                    <Box sx={{ backgroundColor: '#F2F2F2', paddingRight: 0, paddingLeft: 0 }}>
                        <RichText html={bottomHtml} />
                    </Box>
                </PageContainer>
                <Box sx={{ width: width, backgroundColor: '#FFFFFF', height: '1rem' }}></Box>
            </VerticalContainer>
        </>
    );
};

export default OverViewPage;
